import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/lib/types";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(d);
}

export default function AdminMembres() {
  const [members, setMembers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteError, setInviteError] = useState("");

  const [busyId, setBusyId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<Profile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      setCurrentUserId(sessionData.session?.user.id ?? "");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMembers((data ?? []) as Profile[]);
    } catch {
      setLoadError("Impossible de charger les membres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleInvite(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInviteSubmitting(true);
    setInviteMessage("");
    setInviteError("");

    try {
      const { data, error } = await supabase.functions.invoke("manage-members", {
        body: { action: "invite", email, fullName },
      });

      if (error) throw error;

      if (data?.ok) {
        setInviteMessage(
          data.message ?? "Invitation envoyée. La personne recevra un email pour créer son mot de passe."
        );
        setEmail("");
        setFullName("");
        load();
      } else {
        setInviteError(data?.error ?? "Une erreur est survenue.");
      }
    } catch (err) {
      setInviteError(
        err instanceof Error ? err.message : "Une erreur est survenue."
      );
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleToggle(member: Profile) {
    setBusyId(member.id);
    setInviteError("");
    try {
      const { data, error } = await supabase.functions.invoke("manage-members", {
        body: { action: "setAdmin", userId: member.id, isAdmin: !member.is_admin },
      });

      if (error) throw error;

      if (data?.ok) {
        load();
      } else {
        setInviteError(data?.error ?? "Une erreur est survenue.");
      }
    } catch (err) {
      setInviteError(
        err instanceof Error ? err.message : "Une erreur est survenue."
      );
    } finally {
      setBusyId("");
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setInviteError("");
    try {
      const { data, error } = await supabase.functions.invoke("manage-members", {
        body: { action: "remove", userId: confirmDelete.id },
      });

      if (error) throw error;

      if (data?.ok) {
        setConfirmDelete(null);
        load();
      } else {
        setInviteError(data?.error ?? "Une erreur est survenue.");
        setConfirmDelete(null);
      }
    } catch (err) {
      setInviteError(
        err instanceof Error ? err.message : "Une erreur est survenue."
      );
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  const adminCount = members.filter((m) => m.is_admin).length;

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Membres
      </h1>
      <p className="mt-2 text-sm md:text-base text-foreground-600">
        Invitez des membres de votre équipe et gérez leur accès à l'espace
        d'administration.
      </p>

      {/* Invitation */}
      <section className="mt-8 bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
        <h2 className="font-heading text-base font-semibold text-foreground-950">
          Inviter un nouveau membre
        </h2>
        <p className="mt-1 text-sm text-foreground-600">
          La personne recevra un email pour créer son mot de passe. Elle aura
          directement accès à l'espace admin.
        </p>

        <form
          onSubmit={handleInvite}
          className="mt-4 flex flex-col sm:flex-row gap-3"
        >
          <input
            type="text"
            name="full_name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Nom complet"
            className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Adresse email"
            required
            className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
          <button
            type="submit"
            disabled={inviteSubmitting}
            className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {inviteSubmitting ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-user-add-line"></i>
            )}
            Inviter
          </button>
        </form>

        {inviteMessage && (
          <p className="mt-3 flex items-center gap-2 text-sm text-secondary-700">
            <i className="ri-checkbox-circle-line"></i>
            {inviteMessage}
          </p>
        )}
        {inviteError && (
          <p className="mt-3 flex items-center gap-2 text-sm text-accent-700">
            <i className="ri-error-warning-line"></i>
            {inviteError}
          </p>
        )}
      </section>

      {/* Liste */}
      <section className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base font-semibold text-foreground-950">
            Membres de l'équipe
          </h2>
          <span className="text-xs text-foreground-500">
            {adminCount} administrateur{adminCount > 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div className="mt-4 flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : loadError ? (
          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <p className="text-sm text-foreground-600">{loadError}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 rounded-md bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
          </div>
        ) : members.length === 0 ? (
          <p className="mt-4 text-sm text-foreground-600">
            Aucun membre pour le moment.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {members.map((member) => {
              const isSelf = member.id === currentUserId;
              const isBusy = busyId === member.id;
              return (
                <li
                  key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 bg-background-50 rounded-lg border border-background-200 p-4 md:p-5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-user-line text-xl"></i>
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground-950">
                        {member.full_name || "Sans nom"}
                      </span>
                      {isSelf && (
                        <span className="text-[11px] font-medium text-secondary-700 bg-secondary-100 rounded-full px-2 py-0.5">
                          Vous
                        </span>
                      )}
                      <span
                        className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${
                          member.is_admin
                            ? "bg-primary-100 text-primary-700"
                            : "bg-background-100 text-foreground-500"
                        }`}
                      >
                        {member.is_admin ? "Administrateur" : "Membre"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-foreground-600 break-all">
                      {member.email}
                    </p>
                    <p className="mt-0.5 text-xs text-foreground-400">
                      Inscrit le {formatDate(member.created_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleToggle(member)}
                      disabled={isBusy || (isSelf && member.is_admin)}
                      className={`rounded-md px-3 py-2 text-sm font-medium transition cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
                        member.is_admin
                          ? "bg-background-100 text-foreground-700 hover:bg-background-200"
                          : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                      }`}
                    >
                      {isBusy ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : member.is_admin ? (
                        "Retirer l'accès"
                      ) : (
                        "Donner l'accès"
                      )}
                    </button>

                    {!isSelf && (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(member)}
                        disabled={isBusy}
                        title="Supprimer ce membre"
                        className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-100 hover:text-accent-700 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-background-50 rounded-lg border border-background-200 p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Supprimer ce membre ?
            </h3>
            <p className="mt-2 text-sm text-foreground-600">
              Le compte de{" "}
              <span className="font-medium text-foreground-950">
                {confirmDelete.full_name || confirmDelete.email}
              </span>{" "}
              sera définitivement supprimé. Cette action est irréversible.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded-md bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-accent-600 transition cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}